import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import api from "../lib/api";

const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", new_email: "", new_password: "" });

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-sm">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/settings", s); setS(r.data); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const changeCreds = async () => {
    if (!pw.current) { toast.error("Current password required"); return; }
    try {
      const body = { current_password: pw.current };
      if (pw.new_email) body.new_email = pw.new_email;
      if (pw.new_password) body.new_password = pw.new_password;
      await api.post("/auth/change-credentials", body);
      toast.success("Credentials updated");
      setPw({ current: "", new_email: "", new_password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setField = (k) => (v) => setS({ ...s, [k]: v });
  const setWhy = (i, k, v) => {
    const items = [...(s.why_items || [])]; items[i] = { ...items[i], [k]: v };
    setS({ ...s, why_items: items });
  };
  const setAbout = (i, k, v) => {
    const items = [...(s.about_sections || [])]; items[i] = { ...items[i], [k]: v };
    setS({ ...s, about_sections: items });
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline text-[#7A1519]">Website</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Content &amp; Settings</h1>
        </div>
        <button onClick={save} disabled={busy} className="bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2" data-testid="settings-save">
          <Save size={14} /> {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <Card title="Contact information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Address" multiline value={s.address} onChange={setField("address")} testId="set-address" />
          <Field label="Opening Hours" multiline value={s.opening_hours} onChange={setField("opening_hours")} testId="set-hours" />
          <Field label="Phone" value={s.phone} onChange={setField("phone")} testId="set-phone" />
          <Field label="WhatsApp" value={s.whatsapp} onChange={setField("whatsapp")} testId="set-whatsapp" />
          <Field label="Email" value={s.email} onChange={setField("email")} testId="set-email" />
          <Field label="Google Maps Link" value={s.google_maps_link} onChange={setField("google_maps_link")} testId="set-maps" />
        </div>
      </Card>

      <Card title="Social links">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Instagram" value={s.instagram} onChange={setField("instagram")} testId="set-ig" />
          <Field label="Facebook" value={s.facebook} onChange={setField("facebook")} testId="set-fb" />
          <Field label="YouTube" value={s.youtube} onChange={setField("youtube")} testId="set-yt" />
          <Field label="LinkedIn" value={s.linkedin} onChange={setField("linkedin")} testId="set-li" />
          <Field label="Twitter / X" value={s.twitter} onChange={setField("twitter")} testId="set-tw" />
        </div>
      </Card>

      <Card title="Homepage hero">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Headline" value={s.hero_headline} onChange={setField("hero_headline")} testId="set-hero-h" />
          <Field label="Subheading" multiline value={s.hero_subheading} onChange={setField("hero_subheading")} testId="set-hero-sub" />
        </div>
      </Card>

      <Card title="Why Autobahn Cars" action={<button onClick={() => setS({ ...s, why_items: [...s.why_items, { title: "", description: "" }] })} className="text-xs inline-flex items-center gap-1 text-[#7A1519]"><Plus size={12} /> Add point</button>}>
        <div className="space-y-3">
          {s.why_items?.map((w, i) => (
            <div key={i} className="border hairline p-4 rounded-sm bg-[#F7F6F3]/40 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
              <input value={w.title} onChange={(e) => setWhy(i, "title", e.target.value)} placeholder="Title" className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid={`why-title-${i}`} />
              <textarea rows={2} value={w.description} onChange={(e) => setWhy(i, "description", e.target.value)} placeholder="Description" className="bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid={`why-desc-${i}`} />
              <button onClick={() => setS({ ...s, why_items: s.why_items.filter((_, x) => x !== i) })} className="text-[#7A1519] p-2" data-testid={`why-del-${i}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="About sections" action={<button onClick={() => setS({ ...s, about_sections: [...s.about_sections, { heading: "", body: "" }] })} className="text-xs inline-flex items-center gap-1 text-[#7A1519]"><Plus size={12} /> Add section</button>}>
        <div className="space-y-3">
          {s.about_sections?.map((a, i) => (
            <div key={i} className="border hairline p-4 rounded-sm bg-[#F7F6F3]/40 space-y-2">
              <input value={a.heading} onChange={(e) => setAbout(i, "heading", e.target.value)} placeholder="Heading" className="w-full bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid={`about-h-${i}`} />
              <textarea rows={4} value={a.body} onChange={(e) => setAbout(i, "body", e.target.value)} placeholder="Body" className="w-full bg-white border hairline px-3 py-2 rounded-sm text-sm" data-testid={`about-b-${i}`} />
              <button onClick={() => setS({ ...s, about_sections: s.about_sections.filter((_, x) => x !== i) })} className="text-[#7A1519] text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Change admin credentials">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Current Password *" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} type="password" testId="cred-current" />
          <Field label="New Email" value={pw.new_email} onChange={(v) => setPw({ ...pw, new_email: v })} testId="cred-email" />
          <Field label="New Password (min 8)" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} type="password" testId="cred-pw" />
        </div>
        <div className="mt-4">
          <button onClick={changeCreds} className="bg-[#111111] text-white px-5 py-2.5 rounded-sm text-sm" data-testid="cred-save">Update credentials</button>
        </div>
      </Card>
    </div>
  );
};

const Card = ({ title, children, action }) => (
  <div className="bg-white border hairline p-6 rounded-sm">
    <div className="flex items-center justify-between mb-4">
      <div className="overline text-[#7A1519]">{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, type = "text", multiline = false, testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    {multiline ? (
      <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-2.5 text-sm" data-testid={testId} />
    ) : (
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-2.5 text-sm" data-testid={testId} />
    )}
  </div>
);

export default AdminSettings;
